import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState, type FunctionComponent } from 'react'
import useLocalStorageState from 'use-local-storage-state'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/review/[handler]'
import type { API as UserAPI } from '../../../pages/api/user/[handler]'
import { labelScore } from '../../const/ScoreHelper'
import { LARGE_SELECT_HEIGHT } from '../../const/digitContants'
import { hasNoLetters } from '../../const/letterHelper'
import { useTranslate } from '../../contexts/TranslationContext'
import type { ScoreResult } from '../../data/ScoreFragment'
import { useReviewFormDog } from '../../user/ReviewDogContext'
import { useAuth } from '../../user/UserContext'
import { useAgesOptions, useBreedOptions } from '../dogForm/DogInfoForm'
import { ReviewDogInfoSerializer, type ReviewDogInfoSchema } from '../dogForm/ReviewDogInfoSchema'
import { ComboboxInput } from '../form/ComboboxInput'
import { ControlledComboBox } from '../form/ControlledComboBox'
import { TextInput } from '../form/TextInput'
import { ReviewSchema } from '../reviewForm/ReviewSchema'
import { Button } from './Button'
import style from './ReviewForm.module.sass'

const api = createFetcher<API>('/api/review/[handler]')
const userApi = createFetcher<UserAPI>('/api/user/[handler]')

type ReviewFormProps = {
	scoreOptions?: ScoreResult[]
	id: string
	closeForm: () => void
	resetOrder: () => void
	setOrder: () => void
}

type NullableValues<R extends Record<string, unknown>> = {
	[K in keyof R]: R[K] | null
}

type NullableReviewSchema = NullableValues<ReviewSchema>

export const LS_REVIEW_DOG_KEY = 'granule-pod-lupou-reviewDog'

export function useLocalStorageReviewDogInfo() {
	const [rawValue, setRawValue] = useLocalStorageState<string | undefined>(LS_REVIEW_DOG_KEY, {
		storageSync: true,
	})

	const value = useMemo(() => {
		if (rawValue) {
			return ReviewDogInfoSerializer.unserialize(rawValue)
		}
		return null
	}, [rawValue])

	const setValue = useCallback(
		(dogInfo: ReviewDogInfoSchema) => {
			setRawValue(ReviewDogInfoSerializer.serialize(dogInfo))
		},
		[setRawValue],
	)

	const clearValue = useCallback(() => {
		setRawValue(undefined)
	}, [setRawValue])

	return [value, setValue, clearValue] as const
}

export const ReviewForm: FunctionComponent<ReviewFormProps> = ({
	scoreOptions,
	id,
	closeForm,
	resetOrder,
	setOrder,
}) => {
	const auth = useAuth()
	const { reviewDog, saveActiveDog } = useReviewFormDog()
	const loggedUser = auth.status === 'signedIn'
	const translate = useTranslate()

	const username = loggedUser ? auth.user?.fullName : null

	const reviewItemId = id

	const [breed, setBreed] = useState<string | null>(reviewDog?.breed ?? null)
	const breeds = useBreedOptions()
	const ages = useAgesOptions(breed)
	const [age, setAge] = useState<string | null>(reviewDog?.age ?? null)

	const [score, setScore] = useState<string | null>(null)
	const [message, setMessage] = useState<string | null>(null)
	const [title, setTitle] = useState<string | null>(null)
	const [fullName, setFullName] = useState<string | null>(username ?? null)

	const router = useRouter()

	const onChange = useCallback((change: NullableReviewSchema) => {
		console.log({ change })
	}, [])

	const schema = useMemo<NullableReviewSchema>(() => {
		return {
			breed,
			age,
			score,
			id: reviewItemId,
			message,
			fullName,
			title,
		}
	}, [fullName, age, breed, score, reviewItemId, message, title])

	useEffect(() => {
		console.log('onChange', schema)
		onChange?.(schema)
	}, [schema, onChange])

	const validAge = ages.data?.find((item) => item.id === age)

	const reviewInfo = useMemo(() => {
		return ReviewSchema.safeParse({ ...schema })
	}, [schema])

	const scoreMapped = scoreOptions?.map((item) => ({
		id: item.id,
		label: `${item.label}: ${labelScore(Number(item.starsAmount))}`,
	}))

	const queryClient = useQueryClient()

	const clearForm = () => {
		setMessage('')
		setScore(null)
		setTitle('')
	}
	useEffect(() => {
		if (reviewDog?.breed !== breed && !validAge) {
			setAge(null)
		}
	}, [breed, reviewDog?.breed, validAge])

	const onSubmit = async () => {
		if (reviewInfo.success) {
			const reviewDogData = {
				breed: reviewInfo.data.breed,
				age: reviewInfo.data.age,
				userId: loggedUser ? auth.user?.id : undefined,
			}

			if (validAge) {
				saveActiveDog(reviewDogData)
				await api('createReview', {
					type: scoreOptions ? 'ProductReview' : 'BlogPostReview',
					id: reviewItemId,
					fullName: fullName ?? null,
					scoreId: score ? String(score) : null,
					ageId: String(age),
					breedId: String(breed),
					message: String(message),
					title: String(title),
					url: router.asPath,
				})
				if (fullName) {
					await userApi('updateUser', {
						fullName: String(fullName),
					})
				}
				resetOrder()
				setOrder()
				clearForm()
				closeForm()
				if (scoreOptions) {
					queryClient.invalidateQueries({ queryKey: ['productReviews'] })
				} else {
					queryClient.invalidateQueries({ queryKey: ['blogPostReviews'] })
				}

				return
			} else {
				alert('Vámi zadaný věk není vhodný pro zvolené plemeno.')
			}
		}
	}

	return (
		<div className={style.root}>
			<div className={style.inputWrapper}>
				<TextInput
					name="name"
					type="text"
					required
					placeholder={translate('reviewForm.input.placeholder.name')}
					value={fullName ?? ''}
					onChange={(e) => setFullName(e?.target.value)}
				/>
			</div>
			<div className={style.inputWrapper}>
				{scoreOptions ? (
					<ComboboxInput
						state={[score, setScore] as const}
						searchPlaceholder="Vyberte hodnocení"
						items={scoreMapped ?? []}
						height={LARGE_SELECT_HEIGHT}
						isClearable
						isNotSearchable
						name="score"
					/>
				) : (
					<TextInput
						name="title"
						type="text"
						required
						placeholder={translate('reviewForm.input.placeholder.title')}
						value={title ?? ''}
						onChange={(e) => setTitle(e?.target.value)}
					/>
				)}
			</div>
			<textarea
				className={style.inputTextArea}
				placeholder={
					scoreOptions
						? translate('reviewForm.textArea.product.placeholder')
						: translate('reviewForm.textArea.blogPost.placeholder')
				}
				rows={8}
				value={message ?? ''}
				onChange={(e) => setMessage(e?.target.value)}
			/>
			<div className={style.inputGroup}>
				<ControlledComboBox
					key={String('breed' + breeds.isSuccess)}
					state={[breed, setBreed] as const}
					items={breeds.data ?? []}
					height={LARGE_SELECT_HEIGHT}
					name="breed"
					placeholder={translate('dogInfoForm.breed.placeholder')}
				/>
				<ComboboxInput
					key={String('age' + ages.isSuccess)}
					state={[age, setAge] as const}
					searchPlaceholder={translate('dogInfoForm.age.placeholder')}
					items={ages.data ?? []}
					height={LARGE_SELECT_HEIGHT}
					noOptionsMessage={translate('dogInfoForm.breed.first.noOptions')}
					isNotSearchable
					name="age"
				/>
			</div>
			<div className={style.infoWrapper}>
				<div className={style.buttonWrapper}>
					<Button
						buttonType="submit"
						label={translate('reviewForm.addReview.button')}
						onClick={onSubmit}
						disabled={
							scoreOptions
								? !(reviewInfo.success && reviewInfo.data.score) || hasNoLetters(message)
								: !(reviewInfo.success && reviewInfo.data.title) || hasNoLetters(message) || hasNoLetters(title)
						}
					/>
				</div>
			</div>
		</div>
	)
}
